import React from 'react'
import IconButton from '@material-ui/core/IconButton'
import MoreVertIcon from '@material-ui/icons/MoreVert'

import { Button } from 'src/chrome'
import { useRef, useState } from 'src/hooks'

import ContactMenu from './lib/contact-menu'
import styles from './contactActionBar.module.scss'
import { ContactFragment, GroupFragment } from 'src/graphql'
import {
  Div,
  Flex,
  Spacer,
  Text,
  Tooltip,
} from '@sendoutcards/quantum-design-ui'
import { Group } from 'src/contacts/contactTypes'
import HoverTooltip from 'src/chrome/HoverTooltip/HoverTooltip'

interface Props {
  title: string
  totalCount: number
  selectedCount: number
  isViewingGroups: boolean
  onToggleSelectAll: () => void
  onSendCard: () => void
  onViewGroups: () => void
  onStartCampaign: () => void
  onAddToGroup?: () => void
  onDelete: () => void
  onShare?: () => void
  onExport?: () => void
  onManageMembers?: (group: Group) => void
  onEditGroup?: (group: Group) => void
  handleEditContact?: (id?: string) => void
  handleHistoryClick?: (contact: ContactFragment) => void
  selectedGroups?: GroupFragment[]
  contact?: ContactFragment
}

const ContactActionBar: React.FC<Props> = props => {
  const {
    contact,
    handleEditContact,
    handleHistoryClick,
    onToggleSelectAll,
    onSendCard,
    onViewGroups,
    onStartCampaign,
    onAddToGroup,
    onDelete,
    onShare,
    onExport,
    onManageMembers,
    onEditGroup,
    selectedGroups = [],
    title,
    selectedCount,
    totalCount,
    isViewingGroups,
  } = props

  const anchorRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [hoveringOn, setHoveringOn] = useState('')

  const tooltipVariants = {
    hoverOn: {
      opacity: 1,
    },
    hoverOff: {
      opacity: 0,
    },
  }
  const groupOrContact = isViewingGroups ? 'group' : 'contact'
  const tooltipMessageForMany = (
    <Text type="caption">
      You must select one or more {groupOrContact}s
      <br /> to make this button active.
    </Text>
  )

  const tooltipMessage = (
    <Text type="caption">
      You must only select one {groupOrContact} <br /> to make this button
      active.
    </Text>
  )

  const openMenu = () => setIsMenuOpen(true)

  const closeMenu = () => setIsMenuOpen(false)

  return (
    <div className={styles.container}>
      {selectedCount >= 0 && (
        <>
          <div className={styles.actions}>
            <div className={styles.desktopActions}>
              <HoverTooltip
                tooltipChildren={tooltipMessage}
                shouldDisplayTooltip={selectedCount === 0 || selectedCount > 1}
              >
                <Button
                  title="History"
                  className={styles.actionButton}
                  disabled={!handleHistoryClick || !contact}
                  onClick={
                    contact && handleHistoryClick
                      ? () => handleHistoryClick(contact)
                      : () => {}
                  }
                />
              </HoverTooltip>
              {!isViewingGroups && (
                <HoverTooltip
                  shouldDisplayTooltip={
                    selectedCount === 0 || selectedCount > 1
                  }
                  tooltipChildren={tooltipMessage}
                >
                  <Button
                    id={`edit_contact_btn`}
                    title="Edit"
                    icon={'EDIT'}
                    className={styles.actionButton}
                    disabled={!handleEditContact || !contact}
                    onClick={
                      handleEditContact && contact
                        ? () => handleEditContact(contact.id)
                        : () => {}
                    }
                  />
                </HoverTooltip>
              )}
              {onAddToGroup && (
                <HoverTooltip
                  shouldDisplayTooltip={selectedCount === 0}
                  tooltipChildren={tooltipMessageForMany}
                >
                  <Button
                    id={'add_to_group_btn'}
                    title={'Add to Group'}
                    icon={'ADDUSER'}
                    iconColor={'#6f8394'}
                    iconSize={15}
                    style={{ marginRight: 15 }}
                    disabled={selectedCount === 0}
                    onClick={onAddToGroup}
                  />
                </HoverTooltip>
              )}
              {isViewingGroups && (
                <HoverTooltip
                  shouldDisplayTooltip={
                    selectedCount === 0 || selectedCount > 1
                  }
                  tooltipChildren={tooltipMessage}
                >
                  <Button
                    id={'edit_group_btn'}
                    title={'Edit'}
                    icon={'EDIT'}
                    iconColor="#6f8394"
                    iconSize={15}
                    disabled={selectedCount === 0 || selectedCount > 1}
                    onClick={() =>
                      onEditGroup?.({
                        id: selectedGroups[0].id,
                        name: selectedGroups[0].name,
                      })
                    }
                  />
                </HoverTooltip>
              )}
              {isViewingGroups && (
                <HoverTooltip
                  shouldDisplayTooltip={
                    selectedCount === 0 || selectedCount > 1
                  }
                  tooltipChildren={tooltipMessage}
                >
                  <Button
                    id={'manage_members_btn'}
                    title={'Manage Members'}
                    icon={'USER'}
                    iconColor={'#6f8394'}
                    iconSize={15}
                    style={{ marginRight: 15 }}
                    disabled={selectedCount === 0 || selectedCount > 1}
                    onClick={() =>
                      onManageMembers?.({
                        id: selectedGroups[0].id,
                        name: selectedGroups[0].name,
                      })
                    }
                  />
                </HoverTooltip>
              )}
              <HoverTooltip
                shouldDisplayTooltip={selectedCount === 0}
                tooltipChildren={tooltipMessageForMany}
              >
                <Button
                  id={'send_campaign_btn'}
                  title={'Campaign'}
                  icon={'CAMPAIGN'}
                  iconColor={'#6f8394'}
                  iconSize={15}
                  style={{ marginRight: 15 }}
                  disabled={selectedCount === 0}
                  onClick={onStartCampaign}
                />
              </HoverTooltip>
              <HoverTooltip
                shouldDisplayTooltip={selectedCount === 0}
                tooltipChildren={tooltipMessageForMany}
              >
                <Button
                  id={'send_card_btn'}
                  title={'Send Card'}
                  icon={'SEND'}
                  iconColor={'#6f8394'}
                  iconSize={15}
                  disabled={selectedCount === 0}
                  onClick={onSendCard}
                />
              </HoverTooltip>
              <HoverTooltip
                shouldDisplayTooltip={selectedCount === 0}
                tooltipChildren={tooltipMessageForMany}
              >
                <Button
                  id={'delete_contact_btn'}
                  title={'Delete'}
                  icon={'TRASH'}
                  iconColor={'#6f8394'}
                  iconSize={15}
                  style={{ marginRight: 15 }}
                  disabled={selectedCount === 0}
                  onClick={onDelete}
                />
              </HoverTooltip>
              <HoverTooltip
                shouldDisplayTooltip={selectedCount === 0}
                tooltipChildren={tooltipMessageForMany}
              >
                <Button
                  id={'share_contact_btn'}
                  title={'Share'}
                  icon={'GROUP'}
                  iconColor={'#6f8394'}
                  iconSize={15}
                  disabled={selectedCount === 0}
                  onClick={onShare}
                />
              </HoverTooltip>
              <HoverTooltip
                shouldDisplayTooltip={selectedCount === 0}
                tooltipChildren={tooltipMessageForMany}
              >
                <Button
                  id={'export_csv_btn'}
                  title={'Export'}
                  icon={'NEWFILE'}
                  iconColor={'#6f8394'}
                  iconSize={15}
                  disabled={selectedCount === 0}
                  onClick={onExport}
                />
              </HoverTooltip>
            </div>

            <Div
              className={styles.showSm}
              onHoverStart={() => {
                setHoveringOn('Actions')
              }}
              onHoverEnd={() => {
                setHoveringOn('')
              }}
              position="relative"
            >
              <IconButton
                id={'icon_menu'}
                aria-label="more"
                aria-controls="icon-menu"
                aria-haspopup="true"
                onClick={openMenu}
                ref={anchorRef}
              >
                <MoreVertIcon fontSize="large" />
              </IconButton>
              {hoveringOn === 'Actions' && (
                <Flex
                  variants={tooltipVariants}
                  animate={hoveringOn ? 'hoverOn' : 'hoverOff'}
                  initial={'hoverOff'}
                  transition={{ ease: 'easeIn', duration: 0.2 }}
                  top="55px"
                  left="-15%"
                  position="absolute"
                >
                  <Tooltip title="Actions" direction="top" />
                </Flex>
              )}
            </Div>

            <ContactMenu
              anchorEl={anchorRef.current ?? undefined}
              onClose={closeMenu}
              onToggleSelectAll={onToggleSelectAll}
              onSendCard={onSendCard}
              onViewGroups={onViewGroups}
              onStartCampaign={onStartCampaign}
              onAddToGroup={onAddToGroup}
              onDelete={onDelete}
              onShare={onShare}
              onExport={onExport}
              isEverySelected={selectedCount === totalCount}
              isAnySelected={selectedCount > 0}
              isOpen={isMenuOpen}
            />
          </div>

          <div className={styles.contextDetails}>
            <Text type="largeBody">{title}</Text>
            <Spacer space="x2" orientation="horizontal" />
            <Text type="body">{totalCount} total</Text>
            <Spacer space="x2" orientation="horizontal" />
            <Text type="body">{selectedCount} selected</Text>
          </div>
        </>
      )}
    </div>
  )
}

export default ContactActionBar
